import { Button, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { IClienteProgramaFidelidade } from "../../../../../clientes/interface/IClienteProgramaFidelidade";
import { ICategoria } from "../../../../../produtos/interfaces/ICategoria";
import { IProgramaFidelidade } from "../../../../../promocoes/interfaces/IProgramaFidelidade";
import { ICupom } from "../../../../interface/ICupom";
import { LocalPizza } from "@mui/icons-material";
import { useResgateFidelidade } from "../../../../../promocoes/hooks/useResgateFidelidade";
import { useEffect, useMemo, useState } from "react";
import { IProdutoPedido } from "../../../../interface/IProdutoPedido";
import { TipoProduto } from "../../../../../produtos/enum/TipoProduto";
import { v4 } from "uuid";
import { DrawerPreparadoOperacao } from "../../../../../produtos/components/DrawerPreparadoOperacao";
import { DrawerPizzaOperacao } from "../../../../../produtos/components/DrawerPizzaOperacao";

interface FidelidadeItensCardProps {
    programaAtivo: IProgramaFidelidade;
    programaCliente: IClienteProgramaFidelidade;
    cupons: ICupom[];
    aplicarCupom: (cupom: ICupom) => Promise<void>;
    adicionarProduto: (produto: IProdutoPedido) => Promise<IProdutoPedido | undefined>;
    categorias: ICategoria[];
    produtos: IProdutoPedido[];
}

export const FidelidadeItensCard = ({ programaAtivo, programaCliente, cupons, aplicarCupom, adicionarProduto, categorias, produtos }: FidelidadeItensCardProps) => {
    const [produtosAguardandoDrawer, setProdutosAguardandoDrawer] = useState<Set<string>>(new Set());
    const [cupomPrincipalAplicado, setCupomPrincipalAplicado] = useState(false);
    
    const encontrarProdutoPorUuid = (uuid: string) => {
        for (const categoria of categorias) {
            const produto = categoria.produtos?.find((p) => p.uuid === uuid);
            if (produto) return produto;
        }
        return null;
    };

    const { verificarJaResgatado, resgatandoItens, resgatarItens: resgatarItensOriginal, resgatarItem } = useResgateFidelidade({
        aplicarCupom,
        cupons,
    });

    const jaResgatado = verificarJaResgatado(programaCliente.programaFidelidadeUuid) || cupomPrincipalAplicado;
    const isResgatando = resgatandoItens === programaCliente.programaFidelidadeUuid;

    // Wrapper para resgatarItens que também adiciona os produtos após aplicar o cupom principal
    const resgatarItens = async (programaClienteParam: IClienteProgramaFidelidade) => {
        // Marcar que o cupom principal foi aplicado imediatamente
        setCupomPrincipalAplicado(true);
        
        // Aplicar o cupom principal
        await resgatarItensOriginal(programaClienteParam);
        
        // Aguardar um pouco para garantir que o pedido foi atualizado (especialmente no OperacaoEdicaoContext)
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Adicionar produtos automaticamente
        const adicionarProdutoEAplicarCupom = async (produtoPedido: IProdutoPedido) => {
            const produtoAdicionado = await adicionarProduto(produtoPedido);
            if (produtoAdicionado) {
                await resgatarItem(programaClienteParam, produtoAdicionado);
            }
        };

        // Marcar produtos que precisam de drawer para abrir
        const produtosParaDrawer = new Set<string>();

        if (programaAtivo?.produtoUuids) {
            for (const uuid of programaAtivo.produtoUuids) {
                const produto = encontrarProdutoPorUuid(uuid);
                if (!produto) continue;
                
                const produtoAdicionado = produtos.find((p) => p.uuidProdutoReferencia === uuid);
                if (produtoAdicionado) continue; // Já foi adicionado
                
                // Adicionar produtos que não precisam de configuração
                if (produto.tipo === TipoProduto.industrializado) {
                    const produtoPedido: IProdutoPedido = {
                        ...produto,
                        uuid: v4(),
                        uuidProdutoReferencia: produto.uuid,
                        quantidade: 1
                    };
                    await adicionarProdutoEAplicarCupom(produtoPedido);
                } else if (
                    produto.tipo === TipoProduto.preparado && 
                    !produto.configuracao?.habilitarComplementos
                ) {
                    const produtoPedido: IProdutoPedido = {
                        ...produto,
                        uuid: v4(),
                        uuidProdutoReferencia: produto.uuid,
                        quantidade: 1
                    };
                    await adicionarProdutoEAplicarCupom(produtoPedido);
                } else if (
                    (produto.tipo === TipoProduto.preparado && produto.configuracao?.habilitarComplementos) ||
                    produto.tipo === TipoProduto.pizza
                ) {
                    // Produtos que precisam de drawer - marcar para abrir
                    produtosParaDrawer.add(uuid);
                }
            }
        }

        // Atualizar estado para abrir drawers
        if (produtosParaDrawer.size > 0) {
            setProdutosAguardandoDrawer(produtosParaDrawer);
        }
    };

    const progresso =
        programaCliente.saldoPedidos && programaAtivo.quantidadePedidosNecessarios
            ? Math.min((programaCliente.saldoPedidos / programaAtivo.quantidadePedidosNecessarios) * 100, 100)
            : 0;
    const isCompleto = progresso >= 100 || (programaCliente.saldoPedidos && programaAtivo.quantidadePedidosNecessarios && programaCliente.saldoPedidos >= programaAtivo.quantidadePedidosNecessarios);
    const primeiroProduto = programaAtivo.produtoUuids?.[0] ? encontrarProdutoPorUuid(programaAtivo.produtoUuids[0]) : null;
    const nomeProduto = primeiroProduto?.descricao || "Produto Grátis";

    const produtosResgate = useMemo(() => {
        return programaAtivo.produtoUuids?.map((uuid: string) => {
            const produto = encontrarProdutoPorUuid(uuid);
            const produtoAdicionado = produtos.find((p) => p.uuidProdutoReferencia === uuid);
            return {
                produto,
                adicionado: produtoAdicionado ? true : false,
            }
        })
    }, [produtos, programaAtivo, categorias]);

    return (
        <>
            <Paper
                key={programaCliente.uuid}
                elevation={4}
                sx={{
                    p: "0.2rem 1rem",
                    background: isCompleto
                        ? (theme) =>
                            theme.palette.mode === "dark"
                                ? "linear-gradient(135deg, rgba(25, 118, 210, 0.2) 0%, rgba(18, 18, 18, 1) 100%)"
                                : "linear-gradient(135deg, rgba(239, 246, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)"
                        : "transparent",
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
                    <Stack>
                        <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {isCompleto ? "Recompensa" : "Próxima Recompensa"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "14px" }}>
                            {nomeProduto}
                        </Typography>
                    </Stack>
                    <LocalPizza sx={{ fontSize: "24px", color: "text.disabled" }} />
                </Stack>
                <Stack spacing={1.5} sx={{ pb: 1 }}>
                    <LinearProgress variant="determinate" value={progresso} sx={{ height: 8, borderRadius: 4 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 700, color: "text.secondary" }}>
                            {programaCliente.saldoPedidos ?? 0}/{programaAtivo.quantidadePedidosNecessarios ?? 0} PEDIDOS
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => resgatarItens(programaCliente)}
                            disabled={!programaAtivo || !programaCliente || (programaCliente.saldoPedidos ?? 0) < (programaAtivo.quantidadePedidosNecessarios ?? 0) || jaResgatado}
                            sx={{ fontSize: "12px", fontWeight: 700, py: 0.5, px: 1.5, textTransform: "none", minWidth: "auto" }}
                        >
                            {jaResgatado ? "RESGATADO" : isResgatando ? "RESGATANDO..." : "RESGATAR"}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
            {produtosResgate?.map(produtoResgate => {
                if(!programaAtivo) return null;
                // Verificar se foi resgatado OU se está aguardando drawer
                const deveMostrarDrawer = jaResgatado || (produtoResgate.produto && produtosAguardandoDrawer.has(produtoResgate.produto.uuid));
                if(!deveMostrarDrawer) return null;
                if(produtoResgate.produto?.tipo == TipoProduto.industrializado) return null;        
                if(produtoResgate.produto?.tipo == TipoProduto.preparado && !produtoResgate.produto?.configuracao?.habilitarComplementos) return null;
                
                if(produtoResgate.produto?.tipo == TipoProduto.preparado && !produtoResgate.adicionado) return (
                    <DrawerPreparadoOperacao 
                        key={produtoResgate.produto.uuid}
                        open={true}
                        toggleDrawer={() => {
                            // Remover do set quando fechar
                            setProdutosAguardandoDrawer(prev => {
                                const novo = new Set(prev);
                                if (produtoResgate.produto?.uuid) {
                                    novo.delete(produtoResgate.produto.uuid);
                                }
                                return novo;
                            });
                        }}
                        produto={produtoResgate.produto}
                        onSubmit={async (produto) => {
                            // Aguardar o produto ser adicionado antes de aplicar o cupom
                            const produtoAdicionado = await adicionarProduto(produto);
                            if (produtoAdicionado) {
                                await resgatarItem(programaCliente, produtoAdicionado);
                                // Remover do set após adicionar
                                setProdutosAguardandoDrawer(prev => {
                                    const novo = new Set(prev);
                                    if (produtoResgate.produto?.uuid) {
                                        novo.delete(produtoResgate.produto.uuid);
                                    }
                                    return novo;
                                });
                            }
                        }}
                    />
                );

                if(produtoResgate.produto?.tipo == TipoProduto.pizza && !produtoResgate.adicionado) return (
                    <DrawerPizzaOperacao 
                        key={produtoResgate.produto.uuid}
                        open={true}
                        toggleDrawer={() => {
                            // Remover do set quando fechar
                            setProdutosAguardandoDrawer(prev => {
                                const novo = new Set(prev);
                                if (produtoResgate.produto?.uuid) {
                                    novo.delete(produtoResgate.produto.uuid);
                                }
                                return novo;
                            });
                        }}
                        produto={produtoResgate.produto}
                        onSubmit={async (produto) => {
                            // Aguardar o produto ser adicionado antes de aplicar o cupom
                            const produtoAdicionado = await adicionarProduto(produto);
                            if (produtoAdicionado) {
                                await resgatarItem(programaCliente, produtoAdicionado);
                                // Remover do set após adicionar
                                setProdutosAguardandoDrawer(prev => {
                                    const novo = new Set(prev);
                                    if (produtoResgate.produto?.uuid) {
                                        novo.delete(produtoResgate.produto.uuid);
                                    }
                                    return novo;
                                });
                            }
                        }}
                    />
                );
                
                return null;
            })}
        </>
    );
}